import { useEffect, useMemo, useState } from "react";
import {
  AssignTaskRequest,
  ClioService,
  CustomFields,
  MatterPreviewResponse,
  TaskPreviewResponse,
  UsersModel,
} from "../../api";
import {
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { debounce } from "@mui/material/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import FilterableSelect from "../../components/basic/FilterableSelect";

const MySwal = withReactContent(Swal);

const STAFF_TYPES = [
  CustomFields.ASSIGNED_PARALEGAL,
  CustomFields.RESPONSIBLE_ATTORNEY,
  CustomFields.SUPPORTING_ATTY,
  CustomFields.SENIOR_MANAGING_ATTY,
  CustomFields.LEGAL_ASSISTANT,
];

type Props = {
  onAssigned: () => void;
};

export default function AssignTaskForm({ onAssigned }: Props) {
  const [matterOptions, setMatterOptions] = useState<readonly MatterPreviewResponse[]>([]);
  const [matterValue, setMatterValue] = useState<MatterPreviewResponse | null>(null);
  const [matterInputValue, setMatterInputValue] = useState("");
  const [taskInputValue, setTaskInputValue] = useState("");
  const [taskValue, setTaskValue] = useState<TaskPreviewResponse | null>(null);
  const [taskOptions, setTaskOptions] = useState<readonly TaskPreviewResponse[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedUserType, setSelectedUserType] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setMatterValue(null);
    setMatterInputValue("");
    setTaskValue(null);
    setTaskInputValue("");
    setMatterOptions([]);
    setTaskOptions([]);
    setSelectedUser(null);
    setSelectedUserType(null);
  };

  useEffect(() => {
    ClioService.getApiClioClioUsers(true).then(setUsersList);
  }, []);

  const fetchMatters = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioMatters(input).then((response) => {
          const newOptions: MatterPreviewResponse[] = matterValue ? [matterValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setMatterOptions(newOptions);
        });
      }, 200),
    [matterValue]
  );

  useEffect(() => {
    if (matterInputValue === "") {
      setMatterOptions(matterValue ? [matterValue] : []);
      return;
    }
    fetchMatters(matterInputValue);
  }, [matterValue, matterInputValue, fetchMatters]);

  const fetchTasks = useMemo(
    () =>
      debounce((input: string) => {
        ClioService.getApiClioTaskTypes(input).then((response) => {
          const newOptions: TaskPreviewResponse[] = taskValue ? [taskValue] : [];
          if (response) {
            newOptions.push(...response);
          }
          setTaskOptions(newOptions);
        });
      }, 200),
    [taskValue]
  );

  useEffect(() => {
    if (taskInputValue === "") {
      setTaskOptions(taskValue ? [taskValue] : []);
      return;
    }
    fetchTasks(taskInputValue);
  }, [taskValue, taskInputValue, fetchTasks]);

  const canSubmit =
    matterValue?.id != null &&
    taskValue?.id != null &&
    (selectedUser != null || selectedUserType != null);

  const assignTask = () => {
    if (!canSubmit) {
      MySwal.fire({
        icon: "warning",
        title: <p>Please complete all required fields.</p>,
      });
      return;
    }

    const request = {
      matterId: +matterValue!.id!,
      taskTypeId: +taskValue!.id!,
      assigneeId: selectedUser != null ? +selectedUser : null,
      assigneeType: selectedUserType,
    } as AssignTaskRequest;

    setSubmitting(true);
    ClioService.postApiClioAssignTask(request)
      .then(() => {
        MySwal.fire({
          icon: "success",
          title: <p>Task assigned successfully.</p>,
        });
        resetForm();
        onAssigned();
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error assigning task.</p>,
        })
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Assign Clio Task
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            getOptionLabel={(option) =>
              (typeof option === "string" ? option : option.description) as string
            }
            filterOptions={(x) => x}
            options={matterOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={matterValue}
            noOptionsText="Start typing matter"
            onChange={(_, selectedValue) => {
              setMatterValue(selectedValue);
            }}
            onInputChange={(_, newInputValue) => setMatterInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Matter" required fullWidth size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{option.description}</Typography>
              </li>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            getOptionLabel={(option) =>
              (typeof option === "string" ? option : option.name) as string
            }
            filterOptions={(x) => x}
            options={taskOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={taskValue}
            noOptionsText="Start typing task"
            onChange={(_, selectedValue) => setTaskValue(selectedValue)}
            onInputChange={(_, newInputValue) => setTaskInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField {...params} label="Task" required fullWidth size="small" />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FilterableSelect
            label="Assignee"
            fullWidth
            required
            includeAll={false}
            value={selectedUser ?? undefined}
            options={usersList.map((user) => ({
              value: user.id!,
              label: user.name ?? "",
            }))}
            onChange={(userId) => setSelectedUser(String(userId))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth required size="small">
            <InputLabel id="staff-type-label">Staff type</InputLabel>
            <Select
              labelId="staff-type-label"
              label="Staff type"
              value={selectedUserType ?? ""}
              onChange={(e) => setSelectedUserType(e.target.value || null)}
            >
              {STAFF_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5 }}>
        <Button onClick={resetForm} variant="outlined" disabled={submitting}>
          Clear
        </Button>
        <Button
          onClick={assignTask}
          variant="contained"
          disabled={!canSubmit || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          Assign Task
        </Button>
      </Stack>
    </Paper>
  );
}
